import * as React from 'react'

import { observer } from 'mobx-react'

import {
  ILocationClosureInterval,
  IProjectClosedInterval,
} from '~/client/graph'
import IntervalEditor, {
  IIntervalEditorResult,
} from '~/client/src/shared/components/IntervalEditor'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Closure from '~/client/src/shared/models/Closure'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { formatClosureIntervalFmt } from '~/client/src/shared/utils/intervalFormatters'

import { buildDefaultClosure } from '../intervalDefaults'
import EditableTagsCell from './EditableTagsCell'
import { InheritedProjectChip } from './InheritedProjectChip'

interface IProps {
  closures: Closure[]
  projectDateStore: ProjectDateStore
  isBookable: boolean
  onSave: (interval: ILocationClosureInterval, closureId?: string) => void
  onRemove: (closureId: string) => void
  /** How the editor surfaces (popover vs full-panel sheet). */
  presentation?: 'popover' | 'sheet'
  /**
   * Project-level closures this resource inherits. Shown as a non-removable chip beside
   * its own closures - the backend already applies these project-wide, so the chip states
   * a rule that is in force rather than offering a new one. Editing them is Project
   * Settings' business, which is where the chip's popover points.
   */
  inheritedClosures?: IProjectClosedInterval[]
  /** Navigates to the project settings where closures are edited; omitted where it cannot. */
  onEditProjectClosures?: () => void
  isMobile?: boolean
}

function DateClosuresCell({
  closures,
  projectDateStore,
  isBookable,
  onSave,
  onRemove,
  presentation,
  inheritedClosures,
  onEditProjectClosures,
  isMobile,
}: IProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  // null = adding a new closure; otherwise the closure id being edited.
  const [editingId, setEditingId] = React.useState<string | null>(null)

  const editingClosure = editingId
    ? closures.find(closure => closure.id === editingId)
    : undefined

  const openAdd = () => {
    setEditingId(null)
    setIsOpen(true)
  }

  const handleSave = (result: IIntervalEditorResult) => {
    onSave(
      {
        startDate: result.startDate,
        endDate: result.endDate,
        isDaily: !!result.isDaily,
      },
      editingId ?? undefined,
    )
    setIsOpen(false)
  }

  return (
    <EditableTagsCell
      isBookable={isBookable}
      isOpen={isOpen}
      onOpen={openAdd}
      onClose={() => setIsOpen(false)}
      cellAffordance="plus"
      triggerLabel={Localization.translator.dateClosures}
      presentation={presentation}
      sheetHeightMode="content"
      popoverClassName="tw:w-[280px]"
      // One chip for the project's closures as a set, formatted the way Project Settings
      // formats them so the two read alike. Absent when the project has none.
      leadingDisplay={
        inheritedClosures?.length ? (
          <InheritedProjectChip
            label={inheritedClosures
              .map(interval =>
                projectDateStore.getDateYearIntervalToDisplay(
                  interval.startDate,
                  interval.endDate,
                ),
              )
              .join(', ')}
            description={Localization.translator.inheritedClosuresDescription}
            onEditProjectHours={onEditProjectClosures}
            isMobile={isMobile}
          />
        ) : undefined
      }
      tags={closures.map(closure => ({
        key: closure.id,
        label: formatClosureIntervalFmt(
          projectDateStore,
          closure.closureInterval,
          false,
        ),
        // Permit-linked closures are read-only here — no edit, no remove.
        onClick: closure.associatedPermitId
          ? undefined
          : () => {
              setEditingId(closure.id)
              setIsOpen(true)
            },
        onDismiss: closure.associatedPermitId
          ? undefined
          : () => onRemove(closure.id),
      }))}
      popoverContent={
        <IntervalEditor
          key={editingId ?? 'new'}
          initial={
            editingClosure?.closureInterval ??
            buildDefaultClosure(projectDateStore)
          }
          projectDateStore={projectDateStore}
          onSave={handleSave}
          onCancel={() => setIsOpen(false)}
          layout={presentation}
          title={Localization.translator.selectX(
            Localization.translator.dateClosures,
          )}
        />
      }
    />
  )
}

export default observer(DateClosuresCell)
